import { useState, useEffect } from 'react'
import { FileText, Download, Calendar, Loader2, FolderOpen, AlertCircle, ChevronDown, ChevronUp, FileArchive } from 'lucide-react'
import { api } from '../services/api'
import { toast } from 'sonner'

interface Document {
  id: string
  title: string
  description: string | null
  filename: string
  originalName: string
  fileSize: number
  mimeType: string
  category: string
  createdAt: string
}

const categoryLabels: Record<string, string> = {
  estudos: 'Estudos Técnicos de Viabilidade Econômica, Financeira e Jurídica',
  minutas: 'Minutas e Regulamentos',
  consultas: 'Consultas e Audiências Públicas',
  atas: 'Atas do CGPPP',
  edital: 'Minuta do Edital e Anexos',
  contrato: 'Minuta do Contrato e Anexos',
  galeria: 'Galeria do Projeto'
}

const categoryOrder = ['estudos', 'minutas', 'consultas', 'atas', 'edital', 'contrato', 'galeria']

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return bytes + ' B'
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB'
  return (bytes / (1024 * 1024)).toFixed(1) + ' MB'
}

function formatDate(dateStr: string): string {
  return new Date(dateStr).toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: 'long',
    year: 'numeric'
  })
}

export function DocumentsSection() {
  const [documents, setDocuments] = useState<Document[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [downloadingId, setDownloadingId] = useState<string | null>(null)
  const [expandedCategories, setExpandedCategories] = useState<Record<string, boolean>>({})

  useEffect(() => {
    loadDocuments()
  }, [])

  const loadDocuments = async () => {
    setIsLoading(true)
    try {
      const data = await api.getDocuments()
      setDocuments(data)
      
      const categories = [...new Set(data.map(doc => doc.category))]
      const initialExpanded: Record<string, boolean> = {}
      categories.forEach(cat => {
        initialExpanded[cat] = true
      })
      setExpandedCategories(initialExpanded)
    } catch (error) {
      console.error('Erro ao carregar documentos:', error)
      toast.error('Erro ao carregar documentos')
    } finally {
      setIsLoading(false)
    }
  }

  const handleDownload = async (doc: Document) => {
    setDownloadingId(doc.id)
    try {
      await api.downloadDocument(doc.id, doc.originalName)
      toast.success(`Download iniciado: ${doc.title}`)
    } catch (error) {
      console.error('Erro ao baixar:', error)
      toast.error('Erro ao baixar o arquivo')
    } finally {
      setDownloadingId(null)
    }
  }

  const toggleCategory = (category: string) => {
    setExpandedCategories(prev => ({
      ...prev,
      [category]: !prev[category]
    }))
  }

  const expandAll = () => {
    const newExpanded: Record<string, boolean> = {}
    Object.keys(expandedCategories).forEach(cat => {
      newExpanded[cat] = true
    })
    setExpandedCategories(newExpanded)
    toast.success('Todas as categorias expandidas')
  }

  const collapseAll = () => {
    const newExpanded: Record<string, boolean> = {}
    Object.keys(expandedCategories).forEach(cat => {
      newExpanded[cat] = false
    })
    setExpandedCategories(newExpanded)
    toast.success('Todas as categorias recolhidas')
  }

  const groupedDocuments = documents.reduce((acc, doc) => {
    const cat = doc.category
    if (!acc[cat]) acc[cat] = []
    acc[cat].push(doc)
    return acc
  }, {} as Record<string, Document[]>)

  const sortedCategories = categoryOrder.filter(cat => groupedDocuments[cat]?.length > 0)

  const getFileIcon = (mimeType: string) => {
    if (mimeType.includes('zip')) return <FileArchive size={20} className="text-[#51A64D]" />
    return <FileText size={20} className="text-[#51A64D]" />
  }

  const getFileType = (mimeType: string) => {
    if (mimeType.includes('pdf')) return 'PDF'
    if (mimeType.includes('zip')) return 'ZIP'
    return 'DOC'
  }

  if (isLoading) {
    return (
      <section id="documentos" className="py-16 md:py-20 bg-[#F5F7FA]">
        <div className="container text-center">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-[#51A64D] mb-4"></div>
          <p className="text-gray-500">Carregando documentos...</p>
        </div>
      </section>
    )
  }

  return (
    <section id="documentos" className="py-16 md:py-20 bg-[#F5F7FA]">
      <div className="container">
        <div className="max-w-3xl mx-auto text-center mb-12">
          <span className="inline-block text-[#51A64D] text-xs font-semibold uppercase tracking-wider mb-2">
            Acervo Documental
          </span>
          <h2 id="documentos-titulo" className="font-serif text-2xl sm:text-3xl font-bold text-gray-900 mb-3">
            Documentos e Anexos
          </h2>
          <p className="text-gray-600">
            Acesse todos os documentos e estudos do projeto de concessão dos parques urbanos.
          </p>
        </div>

        {documents.length === 0 ? (
          <div className="max-w-2xl mx-auto bg-white rounded-xl p-12 text-center border border-gray-100">
            <FolderOpen size={48} className="mx-auto text-gray-300 mb-4" />
            <p className="text-gray-500 font-medium">Nenhum documento disponível no momento.</p>
            <p className="text-sm text-gray-400 mt-2">
              Os documentos serão publicados em breve conforme o avanço do projeto.
            </p>
          </div>
        ) : (
          <>
            <div className="flex justify-center gap-3 mb-8">
              <button
                onClick={expandAll}
                className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-[#51A64D] bg-green-50 rounded-lg hover:bg-green-100 transition-colors"
              >
                <ChevronDown size={16} />
                Expandir todos
              </button>
              <button
                onClick={collapseAll}
                className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
              >
                <ChevronUp size={16} />
                Recolher todos
              </button>
            </div>

            <div className="max-w-4xl mx-auto space-y-4">
              {sortedCategories.map(category => {
                const isExpanded = expandedCategories[category] ?? true
                const documentsCount = groupedDocuments[category].length
                
                return (
                  <div key={category} className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
                    <button
                      onClick={() => toggleCategory(category)}
                      className="w-full bg-gradient-to-r from-[#51A64D]/5 to-transparent px-6 py-4 hover:bg-[#51A64D]/10 transition-colors flex items-center justify-between group"
                    >
                      <div className="flex items-center gap-3">
                        <div className={`transform transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`}>
                          <ChevronDown size={20} className="text-[#51A64D]" />
                        </div>
                        <h3 className="font-serif font-bold text-lg text-gray-900">
                          {categoryLabels[category] || category}
                        </h3>
                        <span className="inline-flex items-center justify-center px-2 py-0.5 text-xs font-medium bg-[#51A64D]/10 text-[#51A64D] rounded-full">
                          {documentsCount} documento{documentsCount !== 1 ? 's' : ''}
                        </span>
                      </div>
                    </button>
                    
                    <div className={`transition-all duration-300 overflow-hidden ${isExpanded ? 'max-h-[2000px] opacity-100' : 'max-h-0 opacity-0'}`}>
                      <div className="divide-y divide-gray-100 border-t border-gray-100">
                        {groupedDocuments[category].map((doc) => (
                          <div key={doc.id} className="p-5 hover:bg-gray-50 transition-colors">
                            <div className="flex items-start justify-between flex-wrap gap-4">
                              <div className="flex-1 min-w-0">
                                <div className="flex items-start gap-3">
                                  <div className="w-10 h-10 bg-[#51A64D]/10 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5">
                                    {getFileIcon(doc.mimeType)}
                                  </div>
                                  <div>
                                    <h4 className="font-semibold text-gray-900 hover:text-[#51A64D] transition-colors">
                                      {doc.title}
                                    </h4>
                                    {doc.description && (
                                      <p className="text-sm text-gray-500 mt-1">{doc.description}</p>
                                    )}
                                    <div className="flex flex-wrap items-center gap-3 mt-2 text-xs text-gray-400">
                                      <span className="flex items-center gap-1">
                                        <Calendar size={12} />
                                        {formatDate(doc.createdAt)}
                                      </span>
                                      <span>•</span>
                                      <span>{formatFileSize(doc.fileSize)}</span>
                                      <span>•</span>
                                      <span className="uppercase">{getFileType(doc.mimeType)}</span>
                                    </div>
                                  </div>
                                </div>
                              </div>
                              <button
                                onClick={() => handleDownload(doc)}
                                disabled={downloadingId === doc.id}
                                className="flex items-center gap-2 px-4 py-2 bg-[#51A64D] text-white text-sm font-medium rounded-lg hover:bg-[#458f3f] transition-colors disabled:opacity-60 flex-shrink-0"
                              >
                                {downloadingId === doc.id ? (
                                  <>
                                    <Loader2 size={16} className="animate-spin" />
                                    Baixando...
                                  </>
                                ) : (
                                  <>
                                    <Download size={16} />
                                    Baixar
                                  </>
                                )}
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                )
              })}
            </div>
          </>
        )}

        <div className="mt-8 text-center">
          <div className="inline-flex items-center gap-2 text-xs text-gray-400">
            <AlertCircle size={12} />
            <span>Em caso de dúvidas sobre os documentos, entre em contato pelo formulário abaixo.</span>
          </div>
        </div>
      </div>
    </section>
  )
}